import React, { useState, useEffect } from 'react';
import './RPAWizardModal.css';
import { auth } from '../../firebase/config';
import { API_ENDPOINTS, apiClient } from '../../config/api';

// ─────────────────────────────────────────────────────────────────────────────
// SPQ Wizard — field keys are the EXACT California SPQ fillable-PDF AcroForm
// field names (see SPQ_Form_Fields_Schema.json). The save/submit payload maps
// 1:1 onto the PDF with no backend translation. Radio -> "Yes"/"No"/null (or
// the field's enum), checkbox -> boolean, text -> string. Multi-line PDF
// explanation fields (…_line_1/2/3) are entered as a single textarea and split
// by length at payload time.
// ─────────────────────────────────────────────────────────────────────────────

const STEPS = [
  { id: 1, title: 'Property & Seller' },
  { id: 2, title: 'Documents & Statutory' },
  { id: 3, title: 'Repairs & Alterations' },
  { id: 4, title: 'Systems & Disaster' },
  { id: 5, title: 'Water, Pests & Landscape' },
  { id: 6, title: 'HOA & Common Interest' },
  { id: 7, title: 'Title & Neighbors' },
  { id: 8, title: 'Governmental & Other' },
  { id: 9, title: 'Review & Sign' },
];
const TOTAL_STEPS = STEPS.length;

const ICON = (d) => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{d}</svg>;
const STEP_ICONS = {
  1: ICON(<path d="M3 9.5L12 3l9 6.5V20a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9.5z" />),
  2: ICON(<><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></>),
  3: ICON(<path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />),
  4: ICON(<><circle cx="12" cy="12" r="3" /><path d="M12 1v3M12 20v3M4 4l2 2M18 18l2 2M1 12h3M20 12h3M4 20l2-2M18 6l2-2" /></>),
  5: ICON(<path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z" />),
  6: ICON(<><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M3 9h18M3 15h18M9 3v18M15 3v18" /></>),
  7: ICON(<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />),
  8: ICON(<><line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" /></>),
  9: ICON(<><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></>),
};

// Auto-label from a field key: "s8c_septic_system_well_or_propane_tank"
// -> "8C. Septic system well or propane tank".
const labelFor = (key) => {
  const m = key.match(/^s(\d+)([a-z]\d*)?_(.+)$/);
  if (!m) return key.replace(/_/g, ' ');
  const num = m[1] + (m[2] ? m[2].toUpperCase() : '');
  const rest = m[3].replace(/_/g, ' ');
  return `${num}. ${rest.charAt(0).toUpperCase()}${rest.slice(1)}`;
};

// Section config — each section's Yes/No question keys (PDF field names) and
// its single explanation textarea that splits into the PDF's …_line_N fields.
const SECTIONS = [
  { step: 2, title: '§5 — Documents', keys: ['s5a_documents_prepared_before_ownership', 's5b_documents_prepared_for_seller_during_ownership', 's5c_documents_provided_by_others_during_ownership'], exp: ['s5_documents_explanation', 2] },
  { step: 2, title: '§6 — Statutory Disclosures', keys: ['s6a_occupant_death_within_three_years', 's6b_methamphetamine_contamination_order', 's6c_illegal_controlled_substance_release', 's6d_in_or_adjacent_to_industrial_zone', 's6e_industrial_zone_nuisance', 's6f_within_one_mile_of_former_ordnance_location', 's6g_condominium_pud_or_common_interest_subdivision', 's6h_insurance_claims_within_five_years', 's6i_matters_affecting_title', 's6j_noncompliant_plumbing_fixtures', 's6k_elevated_elements_inspection_reports', 's6l_other_undisclosed_material_facts_or_defects'], exp: ['s6_statutory_disclosures_explanation', 3] },
  { step: 3, title: '§7 — Repairs & Alterations', keys: ['s7a_repairs_alterations_or_improvements', 's7b_energy_water_efficiency_or_renewable_improvements', 's7c_recurring_maintenance', 's7d_painted_within_twelve_months', 's7e_built_before_1978', 's7f_acquired_within_eighteen_months_of_offer_acceptance'], exp: ['s7_repairs_and_alterations_explanation', 3] },
  { step: 4, title: '§8 — Structural, Systems & Appliances', keys: ['s8a_structural_system_or_appliance_defects', 's8b_solar_power_system', 's8c_septic_system_well_or_propane_tank', 's8d_leased_water_softener_purifier_or_alarm', 's8e_additional_structure_used_as_dwelling'], exp: ['s8_structural_systems_and_appliances_explanation', 3] },
  { step: 4, title: '§9 — Disaster Relief', keys: ['s9a_disaster_damage_relief_insurance_or_settlement', 's9b_domestic_water_storage_tank_assistance'], exp: ['s9_disaster_relief_explanation', 3] },
  { step: 5, title: '§10 — Water & Mold', keys: ['s10a_water_intrusion_leaks_or_drainage_issues', 's10b_mold_mildew_fungus_or_spores', 's10c_waterways_floods_or_high_water_table'], exp: ['s10_water_and_mold_explanation', 3] },
  { step: 5, title: '§11 — Pets, Animals & Pests', keys: ['s11a_past_or_present_pets', 's11b_livestock_wildlife_insect_or_pest_problems', 's11c_animal_related_odors_stains_or_damage', 's11d_pest_or_odor_treatment_or_damage_repair'], exp: ['s11_pets_animals_and_pests_explanation', 3] },
  { step: 5, title: '§12 — Boundaries, Access & Use', keys: ['s12a_surveys_easements_encroachments_or_boundary_disputes', 's12b_property_use_or_access_by_others', 's12c_seller_use_of_neighboring_property'], exp: ['s12_boundaries_access_and_use_explanation', 3] },
  { step: 5, title: '§13 — Landscaping, Pool & Spa', keys: ['s13a_vegetation_disease_or_infestation', 's13b_operational_sprinklers', 's13c_pool_heater_present', 's13d_spa_heater_present', 's13e_landscaping_pool_spa_or_water_feature_problems'], exp: ['s13_landscaping_pool_and_spa_explanation', 3] },
  { step: 6, title: '§14 — HOA & Common Interest', keys: ['s14a_condominium_pud_or_common_interest_subdivision', 's14b_hoa_authority', 's14c_coowned_common_areas', 's14d_ccrs_or_deed_restrictions', 's14e_hoa_dues_assessments_rules_insurance_or_disputes', 's14f_restrictions_or_hoa_authority_over_improvements'], exp: ['s14_hoa_and_subdivisions_explanation', 3] },
  { step: 7, title: '§15 — Title, Ownership & Claims', keys: ['s15a_other_ownership_interests', 's15b_leases_options_or_title_use_claims', 's15c_litigation_liens_defaults_or_legal_proceedings', 's15d_shared_features_with_adjoining_landowners', 's15e_encroachments_easements_or_boundary_matters', 's15f_private_transfer_fees', 's15g_pace_or_improvement_financing_liens', 's15h_improvements_paid_through_property_tax_assessment'], exp: ['s15_title_ownership_and_claims_explanation', 3] },
  { step: 7, title: '§16 — Neighbors & Neighborhood', keys: ['s16a_neighborhood_noise_nuisance_or_problems', 's16b_neighbor_disputes_affecting_property'], exp: ['s16_neighbors_and_neighborhood_explanation', 3] },
  { step: 8, title: '§17 — Governmental', keys: ['s17a_eminent_domain_condemnation_annexation_or_zoning_changes', 's17b_rent_occupancy_improvement_or_retrofit_restrictions', 's17c_building_or_use_moratoria', 's17d_gas_appliance_replacement_restrictions', 's17e_bonds_assessments_or_fees_not_on_tax_bill', 's17f_government_facility_construction_changes_or_closure', 's17g_vegetation_or_flammable_material_requirements', 's17h_protected_habitat', 's17i_historic_designation_or_district', 's17j_water_surcharges_penalties_or_groundwater_restrictions', 's17k_postal_city_differs_from_jurisdiction_city'], exp: ['s17_governmental_explanation', 3] },
  { step: 8, title: '§18 — Other Disclosures', keys: ['s18a_past_or_present_smoking_or_vaping', 's18b_tobacco_or_nicotine_residue', 's18c_cannabis_cultivation_or_related_alterations', 's18d_originally_manufactured_or_mobile_home', 's18e_currently_tenant_occupied', 's18f_previously_tenant_occupied'], exp: ['s18_other_disclosures_explanation', 3] },
  { step: 8, title: '§19 — Material Facts', keys: ['s19a_other_undisclosed_material_facts'], exp: ['s19_material_facts_explanation', 3] },
];

// Conditional sub-questions: parent key -> child keys shown only when parent === 'Yes'.
const SUBQ = {
  s7e_built_before_1978: ['s7e1_lead_paint_surface_renovations'],
  s7e1_lead_paint_surface_renovations: ['s7e2_renovations_compliant_with_epa_lead_rule'],
  s7f_acquired_within_eighteen_months_of_offer_acceptance: ['s7f1_contractor_improvements_during_ownership'],
  s8e_additional_structure_used_as_dwelling: ['s8e1_dwelling_has_separate_utilities_and_meters', 's8e2_dwelling_has_adu_permit_or_approval'],
  s9a_disaster_damage_relief_insurance_or_settlement: ['s9a1_flood_assistance_requires_flood_insurance'],
  s13b_operational_sprinklers: ['s13b2_vegetation_areas_not_covered_by_sprinklers'],
  s13c_pool_heater_present: ['s13c1_pool_heater_operational'],
  s13d_spa_heater_present: ['s13d1_spa_heater_operational'],
  s14f_restrictions_or_hoa_authority_over_improvements: ['s14f1_improvements_inconsistent_with_restrictions', 's14f2_improvements_without_hoa_approval'],
  s18e_currently_tenant_occupied: ['s18e1_tenancy_start_photographs_available'],
};

// Every PDF line-field group behind a single textarea.
const EXPLANATION_GROUPS = Object.fromEntries(
  SECTIONS.map((s) => [s.exp[0], Array.from({ length: s.exp[1] }, (_, i) => `${s.exp[0]}_line_${i + 1}`)]),
);

const MAX_CHARS_PER_LINE = 95;
function splitIntoLines(text, lineKeys) {
  const out = {};
  lineKeys.forEach((k) => { out[k] = ''; });
  const words = String(text || '').trim().split(/\s+/).filter(Boolean);
  if (!words.length) return out;
  const lines = [];
  let cur = '';
  words.forEach((w) => {
    const cand = cur ? `${cur} ${w}` : w;
    const last = lines.length === lineKeys.length - 1;
    if (last) { cur = cand; return; }
    if (cand.length <= MAX_CHARS_PER_LINE || !cur) cur = cand;
    else { lines.push(cur); cur = w; }
  });
  if (cur) lines.push(cur);
  lineKeys.forEach((k, i) => { out[k] = lines[i] || ''; });
  return out;
}
function joinLines(data, lineKeys) {
  return lineKeys.map((k) => data[k]).filter(Boolean).join(' ').trim();
}

// ── Sub-components ──────────────────────────────────────────────────────────────
function InfoBox({ type = 'info', children }) { return <div className={`rpa-info-box rpa-info-box--${type}`}>{children}</div>; }
function SectionTitle({ children }) { return <div className="rpa-section-title">{children}</div>; }
function Field({ label, hint, children }) {
  return (
    <div className="rpa-field">
      {label && <label className="rpa-field-label">{label}</label>}
      {hint && <div className="rpa-field-hint">{hint}</div>}
      {children}
    </div>
  );
}
function YesNo({ value, onChange, options }) {
  const opts = options || [['Yes', 'Yes'], ['No', 'No']];
  return (
    <div className="tds-yn-group" style={{ minWidth: 120 }}>
      {opts.map(([val, lbl]) => (
        <button key={val} type="button"
          className={`tds-yn-btn ${val === 'No' ? 'no' : 'yes'}${value === val ? ' selected' : ''}`}
          onClick={() => onChange(value === val ? null : val)}>{lbl}</button>
      ))}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
function SPQWizardModal({ isOpen, onClose, onComplete, onReviewComplete, onProgressUpdate, readOnly, transaction, draftData }) {
  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState(new Set());
  const [confirmed, setConfirmed] = useState(false);
  const [draftId, setDraftId] = useState(draftData?.id || draftData?.wizardId || null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [fields, setFields] = useState({});
  const [texts, setTexts] = useState({});

  const cb = (k) => fields[k] === true;
  const setCb = (k, v) => setFields((p) => ({ ...p, [k]: v }));
  const rad = (k) => (fields[k] ?? null);
  const setRad = (k, v) => setFields((p) => ({ ...p, [k]: v }));
  const txt = (k) => (fields[k] ?? '');
  const setTxt = (k, v) => setFields((p) => ({ ...p, [k]: v }));
  const exp = (k) => (texts[k] ?? '');
  const setExp = (k, v) => setTexts((p) => ({ ...p, [k]: v }));

  const getFirebaseToken = () => new Promise((resolve, reject) => {
    if (auth.currentUser) { auth.currentUser.getIdToken(true).then(resolve).catch(reject); return; }
    const { onAuthStateChanged } = require('firebase/auth');
    const unsub = onAuthStateChanged(auth, (user) => {
      unsub();
      if (user) user.getIdToken(true).then(resolve).catch(reject);
      else reject(new Error('Not authenticated'));
    });
    setTimeout(() => reject(new Error('Auth timeout')), 10000);
  });

  const getTxnAddress = () => transaction?.address || transaction?.propertyAddress || '';
  const getTxnCity = () => transaction?.city || '';
  const getTxnCounty = () => transaction?.county || transaction?.propertyCounty || '';

  // Per-step PDF field keys (drives save payloads). Step 1 = identity; sections
  // contribute their question keys + any sub-question keys.
  const STEP_FIELD_KEYS = {
    1: ['property_street_address_line_1', 'property_city', 'property_county', 'property_assessor_parcel_number',
        'property_has_additional_dwelling_units', 'disclosure_applies_only_to_specified_units', 'disclosed_unit_numbers_page_1'],
  };
  SECTIONS.forEach((s) => {
    const arr = STEP_FIELD_KEYS[s.step] || (STEP_FIELD_KEYS[s.step] = []);
    s.keys.forEach((k) => {
      arr.push(k);
      (SUBQ[k] || []).forEach((c) => { arr.push(c); (SUBQ[c] || []).forEach((cc) => arr.push(cc)); });
    });
  });
  (STEP_FIELD_KEYS[5] = STEP_FIELD_KEYS[5] || []).push('s13b1_sprinkler_operation_type', 's11d_pest_treatment_date_and_provider');
  (STEP_FIELD_KEYS[8] = STEP_FIELD_KEYS[8] || []).push('s19b_additional_comments_addendum_attached');

  const stepExplanations = (step) => SECTIONS.filter((s) => s.step === step).map((s) => s.exp[0]);

  const getStepPayload = (stepNumber) => {
    if (stepNumber === TOTAL_STEPS) return { confirmed, submittedAt: new Date().toISOString() };
    const out = {};
    (STEP_FIELD_KEYS[stepNumber] || []).forEach((k) => { if (fields[k] !== undefined) out[k] = fields[k]; });
    stepExplanations(stepNumber).forEach((logicalKey) => {
      Object.assign(out, splitIntoLines(exp(logicalKey), EXPLANATION_GROUPS[logicalKey]));
    });
    return out;
  };

  const getCompletedStepsFromDraft = (draft) => {
    const completed = new Set();
    Object.entries(draft?.steps || {}).forEach(([num, data]) => {
      if (data?.stepStatus === 'COMPLETED' || data?.completed === true) completed.add(Number(num));
    });
    if (completed.size === 0 && Array.isArray(draft?.completedSteps)) draft.completedSteps.forEach((n) => completed.add(Number(n)));
    return Array.from(completed).filter((n) => n >= 1 && n <= TOTAL_STEPS).sort((a, b) => a - b);
  };

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    if (draftData) {
      const restoredStep = draftData.currentStep || 1;
      setCurrentStep(restoredStep);
      setDraftId(draftData.id || draftData.wizardId || null);
      const completedNums = getCompletedStepsFromDraft(draftData);
      setCompletedSteps(new Set(completedNums));
      if (onProgressUpdate) onProgressUpdate(completedNums, restoredStep);

      const mergedFields = {};
      const mergedTexts = {};
      Object.values(draftData.steps || {}).forEach((stepData) => {
        Object.entries(stepData?.data || {}).forEach(([k, v]) => {
          if (k === 'confirmed' || k === 'submittedAt') return;
          mergedFields[k] = v;
        });
      });
      Object.entries(EXPLANATION_GROUPS).forEach(([logicalKey, lineKeys]) => {
        const joined = joinLines(mergedFields, lineKeys);
        if (joined) mergedTexts[logicalKey] = joined;
      });
      if (!mergedFields.property_street_address_line_1) mergedFields.property_street_address_line_1 = getTxnAddress();
      if (!mergedFields.property_city) mergedFields.property_city = getTxnCity();
      if (!mergedFields.property_county) mergedFields.property_county = getTxnCounty();
      setFields(mergedFields);
      setTexts(mergedTexts);
      return;
    }
    setCurrentStep(1);
    setFields({ property_street_address_line_1: getTxnAddress(), property_city: getTxnCity(), property_county: getTxnCounty() });
    setTexts({});
  }, [isOpen, draftData, transaction]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isOpen) return null;

  const progressPercent = Math.round(((currentStep - 1) / (TOTAL_STEPS - 1)) * 100);
  const propertyLabel = txt('property_street_address_line_1')
    ? `${txt('property_street_address_line_1')}${txt('property_city') ? `, ${txt('property_city')}` : ''}`
    : (transaction && (transaction.address || transaction.propertyAddress)) || 'New SPQ';

  const goToStep = async (step, isForward) => {
    if (!readOnly && draftId && transaction?.id) {
      if (isForward) {
        const newCompleted = new Set([...completedSteps, currentStep]);
        setCompletedSteps(newCompleted);
        setIsSaving(true); setSaveError(null);
        try {
          const token = await getFirebaseToken();
          await apiClient.put(API_ENDPOINTS.spqWizardSaveStep(transaction.id, draftId, currentStep, true),
            { data: getStepPayload(currentStep) }, { headers: { Authorization: `Bearer ${token}` } });
          if (onProgressUpdate) onProgressUpdate([...newCompleted], step);
        } catch (err) {
          console.warn('[SPQ] Step save failed:', err?.message);
          setSaveError('Failed to save step. Your progress may not be saved.');
          setIsSaving(false); return;
        } finally { setIsSaving(false); }
      } else {
        apiClient.put(API_ENDPOINTS.spqWizardSaveStep(transaction.id, draftId, currentStep, false),
          { data: getStepPayload(currentStep) }, { headers: { Authorization: `Bearer ${await getFirebaseToken()}` } })
          .catch((err) => console.warn('[SPQ] Back/jump save failed:', err?.message));
      }
    }
    setCurrentStep(step);
  };
  const handleNext = async () => { await goToStep(Math.min(currentStep + 1, TOTAL_STEPS), true); };
  const handleBack = () => { if (currentStep > 1) goToStep(currentStep - 1, false); };
  const handleStepClick = (stepId) => { goToStep(stepId, stepId > currentStep); };

  const handleGenerateSPQ = async () => {
    if (!confirmed) return;
    if (draftId && transaction?.id) {
      setIsSaving(true); setSaveError(null);
      try {
        const token = await getFirebaseToken();
        for (let step = 1; step < TOTAL_STEPS; step += 1) {
          if (!completedSteps.has(step)) {
            await apiClient.put(API_ENDPOINTS.spqWizardSaveStep(transaction.id, draftId, step, true),
              { data: getStepPayload(step) }, { headers: { Authorization: `Bearer ${token}` } });
          }
        }
        await apiClient.put(API_ENDPOINTS.spqWizardSaveStep(transaction.id, draftId, TOTAL_STEPS, true),
          { data: getStepPayload(TOTAL_STEPS) }, { headers: { Authorization: `Bearer ${token}` } });
        await apiClient.post(API_ENDPOINTS.spqWizardSubmit(transaction.id, draftId), {},
          { headers: { Authorization: `Bearer ${token}` } });
        const all = Array.from({ length: TOTAL_STEPS }, (_, i) => i + 1);
        setCompletedSteps(new Set(all));
        if (onProgressUpdate) onProgressUpdate(all, TOTAL_STEPS);
      } catch {
        setSaveError('Failed to submit SPQ. Please try again.');
        setIsSaving(false); return;
      }
      setIsSaving(false);
    }
    if (onComplete) onComplete(); else onClose();
  };

  const handleCompleteReview = async () => {
    if (!readOnly || !transaction?.id) { onClose(); return; }
    const propertyId = draftData?.interestedPropertyId || transaction?.propertyId;
    if (!propertyId) { setSaveError('Missing property reference for review completion.'); return; }
    setIsSaving(true); setSaveError(null);
    try {
      const token = await getFirebaseToken();
      await apiClient.post(API_ENDPOINTS.spqWizardLock(transaction.id, propertyId), {},
        { headers: { Authorization: `Bearer ${token}` } });
      if (onReviewComplete) await onReviewComplete(); else onClose();
    } catch (err) {
      console.warn('[SPQ] Review completion failed:', err?.message);
      setSaveError('Failed to complete SPQ review. Please try again.');
    } finally { setIsSaving(false); }
  };

  const handleOverlayClick = (e) => { if (e.target === e.currentTarget) onClose(); };

  // ── Renderers ────────────────────────────────────────────────────────────────
  const QRow = ({ fieldKey, options, label }) => (
    <div className={`tds-defect-row${rad(fieldKey) === 'Yes' ? ' flagged' : ''}`}>
      <span className="tds-defect-label">{label || labelFor(fieldKey)}</span>
      <YesNo value={rad(fieldKey)} onChange={(v) => setRad(fieldKey, v)} options={options} />
    </div>
  );

  const QWithSubs = ({ fieldKey }) => (
    <>
      <QRow fieldKey={fieldKey} />
      {fieldKey === 's13b_operational_sprinklers' && rad(fieldKey) === 'Yes' && (
        <div className="spq-sub-row" style={{ paddingLeft: 18 }}>
          <QRow fieldKey="s13b1_sprinkler_operation_type" label="13B(1). Sprinkler operation type" options={[['Automatic', 'Automatic'], ['Manual', 'Manual']]} />
        </div>
      )}
      {(SUBQ[fieldKey] || []).map((childKey) => (
        rad(fieldKey) === 'Yes' && (
          <div key={childKey} className="spq-sub-row" style={{ paddingLeft: 18 }}>
            <QRow fieldKey={childKey} />
            {(SUBQ[childKey] || []).map((gc) => (
              rad(childKey) === 'Yes' && <div key={gc} style={{ paddingLeft: 18 }}><QRow fieldKey={gc} /></div>
            ))}
          </div>
        )
      ))}
      {fieldKey === 's11d_pest_or_odor_treatment_or_damage_repair' && rad(fieldKey) === 'Yes' && (
        <div className="spq-sub-row" style={{ paddingLeft: 18 }}>
          <Field label="Treatment date & provider">
            <input className="rpa-input" value={txt('s11d_pest_treatment_date_and_provider')} onChange={(e) => setTxt('s11d_pest_treatment_date_and_provider', e.target.value)} />
          </Field>
        </div>
      )}
    </>
  );

  const renderSection = (sec) => (
    <div className="rpa-card" key={sec.title}>
      <SectionTitle>{sec.title}</SectionTitle>
      {sec.keys.map((k) => <QWithSubs key={k} fieldKey={k} />)}
      {sec.title.startsWith('§19') && (
        <label className="tds-cb-row" style={{ marginTop: 8 }} onClick={() => setCb('s19b_additional_comments_addendum_attached', !cb('s19b_additional_comments_addendum_attached'))}>
          <span className="tds-cb-box">{cb('s19b_additional_comments_addendum_attached') ? '✓' : ''}</span>
          <span className="tds-cb-label">19B. Additional comments addendum attached</span>
        </label>
      )}
      <Field label={`Explanation for any "Yes" in ${sec.title}`}>
        <textarea className="rpa-input" style={{ minHeight: 64, resize: 'vertical' }}
          placeholder="Reference the question number and explain…"
          value={exp(sec.exp[0])} onChange={(e) => setExp(sec.exp[0], e.target.value)} />
      </Field>
    </div>
  );

  const renderStep1 = () => (
    <div>
      <div className="rpa-card">
        <InfoBox type="info"><strong>SPQ — Seller Property Questionnaire.</strong> Supplements the TDS. Seller discloses known material items affecting the value or desirability of the property, based on actual knowledge.</InfoBox>
        <SectionTitle>Property</SectionTitle>
        <Field label="Property Address"><input className="rpa-input" value={txt('property_street_address_line_1')} onChange={(e) => setTxt('property_street_address_line_1', e.target.value)} placeholder="e.g. 2729 Gobat Ave" /></Field>
        <div className="rpa-field-row">
          <Field label="City"><input className="rpa-input" value={txt('property_city')} onChange={(e) => setTxt('property_city', e.target.value)} /></Field>
          <Field label="County"><input className="rpa-input" value={txt('property_county')} onChange={(e) => setTxt('property_county', e.target.value)} /></Field>
          <Field label="APN"><input className="rpa-input" value={txt('property_assessor_parcel_number')} onChange={(e) => setTxt('property_assessor_parcel_number', e.target.value)} placeholder="e.g. 345-678-12" /></Field>
        </div>
        <label className="tds-cb-row" onClick={() => setCb('property_has_additional_dwelling_units', !cb('property_has_additional_dwelling_units'))}>
          <span className="tds-cb-box">{cb('property_has_additional_dwelling_units') ? '✓' : ''}</span>
          <span className="tds-cb-label">Property is a duplex, triplex, fourplex, or has additional dwelling units</span>
        </label>
        {cb('property_has_additional_dwelling_units') && (
          <div className="rpa-toggle-content">
            <label className="tds-cb-row" onClick={() => setCb('disclosure_applies_only_to_specified_units', !cb('disclosure_applies_only_to_specified_units'))}>
              <span className="tds-cb-box">{cb('disclosure_applies_only_to_specified_units') ? '✓' : ''}</span>
              <span className="tds-cb-label">This SPQ is for only specific unit(s)</span>
            </label>
            {cb('disclosure_applies_only_to_specified_units') && (
              <Field label="Unit(s)"><input className="rpa-input" value={txt('disclosed_unit_numbers_page_1')} onChange={(e) => setTxt('disclosed_unit_numbers_page_1', e.target.value)} /></Field>
            )}
          </div>
        )}
      </div>
    </div>
  );

  const renderStep9 = () => {
    const answered = SECTIONS.reduce((acc, s) => acc + s.keys.filter((k) => rad(k) != null).length, 0);
    const totalQ = SECTIONS.reduce((acc, s) => acc + s.keys.length, 0);
    const yesCount = SECTIONS.reduce((acc, s) => acc + s.keys.filter((k) => rad(k) === 'Yes').length, 0);
    return (
      <div className="rpa-card">
        <InfoBox type="info"><strong>Review &amp; Sign.</strong> Confirm the SPQ disclosures below. Signatures are collected via DocuSign after generation.</InfoBox>
        <div style={{ fontSize: 13, color: '#555', lineHeight: 1.6 }}>
          <div><strong>Property:</strong> {txt('property_street_address_line_1') || '—'}{txt('property_city') ? `, ${txt('property_city')}` : ''}</div>
          <div><strong>Questions answered:</strong> {answered} / {totalQ}</div>
          <div><strong>"Yes" answers:</strong> {yesCount}</div>
        </div>
        <label className="tds-confirm-row" style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginTop: 16, cursor: 'pointer' }}>
          <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} style={{ marginTop: 3 }} />
          <span style={{ fontSize: 13 }}>I confirm the above disclosures are true and complete to the best of the Seller's knowledge, and authorize generation of the SPQ for signature.</span>
        </label>
      </div>
    );
  };

  const renderCurrentStep = () => {
    if (currentStep === 1) return renderStep1();
    if (currentStep === TOTAL_STEPS) return renderStep9();
    return <div>{SECTIONS.filter((s) => s.step === currentStep).map(renderSection)}</div>;
  };

  return (
    <div className="rpa-overlay" role="dialog" aria-modal="true" aria-label="SPQ Wizard" onClick={handleOverlayClick}>
      <div className="rpa-modal" onClick={(e) => e.stopPropagation()}>
        <div className="rpa-header">
          <div className="rpa-header-left">
            <span className="rpa-header-title">SPQ Wizard</span>
            {readOnly && <span style={{ fontSize: 11, fontWeight: 600, color: '#7C3AED', background: '#EDE9FE', borderRadius: 4, padding: '2px 7px', marginLeft: 8 }}>READ ONLY</span>}
            <span className="rpa-header-sub">{propertyLabel}</span>
          </div>
          <button className="rpa-close-btn" onClick={onClose} aria-label="Close">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
          </button>
        </div>
        <div className="rpa-progress-track"><div className="rpa-progress-bar" style={{ width: `${progressPercent}%` }} /></div>
        <div className="rpa-body">
          <nav className="rpa-sidebar" aria-label="Wizard steps">
            {STEPS.map((step) => {
              const isDone = completedSteps.has(step.id);
              const isActive = step.id === currentStep;
              return (
                <button key={step.id} type="button" className={`rpa-step-item${isActive ? ' active' : ''}${isDone ? ' done' : ''}`} onClick={() => handleStepClick(step.id)} aria-current={isActive ? 'step' : undefined}>
                  <span className="rpa-step-icon">{STEP_ICONS[step.id]}</span>
                  <span className="rpa-step-meta">
                    <span className="rpa-step-num">Step {step.id}</span>
                    <span className="rpa-step-title">{step.title}</span>
                  </span>
                  {isDone && <span className="rpa-step-check">✓</span>}
                </button>
              );
            })}
          </nav>
          <main className="rpa-content">
            <div style={readOnly ? { pointerEvents: 'none', userSelect: 'none' } : undefined}>{renderCurrentStep()}</div>
          </main>
        </div>
        <div className="rpa-footer">
          <div className="rpa-footer-left">{currentStep > 1 && <button className="rpa-btn rpa-btn-ghost" onClick={handleBack}>← Back</button>}</div>
          <div className="rpa-footer-center"><span className="rpa-footer-progress">Step {currentStep} of {TOTAL_STEPS}</span></div>
          <div className="rpa-footer-right">
            {saveError && <span style={{ fontSize: 12, color: '#C0392B', marginRight: 12 }}>{saveError}</span>}
            {currentStep < TOTAL_STEPS && (
              <button className="rpa-btn rpa-btn-primary" onClick={handleNext} disabled={!readOnly && isSaving}>
                {!readOnly && isSaving ? 'Saving…' : readOnly ? 'Next →' : 'Save & Continue →'}
              </button>
            )}
            {currentStep === TOTAL_STEPS && (
              readOnly ? (
                <button className="rpa-btn rpa-btn-primary" onClick={handleCompleteReview} disabled={isSaving}>{isSaving ? 'Completing…' : 'Complete Review'}</button>
              ) : (
                <button className={`rpa-btn${confirmed && !isSaving ? ' rpa-btn-primary' : ' rpa-btn-disabled'}`} onClick={handleGenerateSPQ} disabled={!confirmed || isSaving}>{isSaving ? 'Submitting…' : 'Generate SPQ'}</button>
              )
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default SPQWizardModal;
